import React from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import StorageIcon from '@mui/icons-material/Storage'
import SecurityIcon from '@mui/icons-material/Security'
import LinkIcon from '@mui/icons-material/Link'

interface Props {
  open: boolean
  onClose: () => void
}

const Step: React.FC<{ title: string; children: React.ReactNode; icon?: React.ReactNode }> = ({ title, children, icon }) => (
  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Box>
)

const BackblazeHowToDialog: React.FC<Props> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon color="primary" />
        Backblaze B2 — How to connect
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Follow these simple steps to connect Backblaze B2 and upload media directly from this admin page.
          </Typography>

          <Step title="1) Sign in to Backblaze" icon={<LinkIcon color="primary" />}>
            Go to <b>backblaze.com</b> and sign in or create a new account. Open the <b>B2 Cloud Storage</b> area.
          </Step>

          <Step title="2) Create a Bucket" icon={<StorageIcon color="primary" />}>
            Create a new <b>Bucket</b>. Pick any name (for example: <i>my-website-media</i>). You can keep it private.
          </Step>

          <Step title="3) Create Access Keys" icon={<SecurityIcon color="primary" />}>
            In <b>Application Keys</b>, create a new key. You will get two values:
            <br /><Chip size="small" label="keyID" sx={{ mr: 1 }} /> and <Chip size="small" label="applicationKey" />
            <br />Keep them safe. You will paste them here in the settings.
          </Step>

          <Step title="4) Copy connection details" icon={<LinkIcon color="primary" />}>
            Note these values from Backblaze:
            <br />
            • <b>Bucket</b> (the name you created)
            <br />
            • <b>Region</b> (for example: <i>eu-central-003</i>)
            <br />
            • <b>Endpoint</b> (for example: <i>https://s3.eu-central-003.backblazeb2.com</i>)
            <br />
            • <b>Public Base URL</b> (optional — your CDN or public link base)
            <br />
            • <b>Folder Prefix</b> (where files will be placed, e.g. <i>uploads/</i>)
          </Step>

          <Step title="5) Paste everything in this page" icon={<CloudUploadIcon color="primary" />}>
            In <b>Website Settings → Backblaze</b>, paste <b>keyID</b>, <b>applicationKey</b>, and the values above. Turn on <b>Enable Backblaze</b> and click <b>Save Backblaze Settings</b>.
          </Step>

          <Step title="6) Test it" icon={<CloudUploadIcon color="primary" />}>
            Click <b>Choose Test File</b> and then <b>Test Upload</b>. If it succeeds, your media uploads are ready to use.
          </Step>

          <Step title="(Optional) Make files public" icon={<SecurityIcon color="primary" />}>
            If you want public links, make the bucket public or use a CDN. You can paste the public link base in <b>Public Base URL</b>.
          </Step>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default BackblazeHowToDialog
